import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Warning, MapPin, ArrowRight } from "@phosphor-icons/react";
import { regionHealth, healthBand, toneColor, ndviStatus, soilMoistureStatus, yieldStatus } from "@/lib/laymen";

/**
 * Top 5 Stress-Regionen: automatisch sortiert nach schlechtester Health Score.
 * Zeigt sofort auf dem Dashboard, wo Handlungsbedarf besteht.
 */
export default function StressLeaderboard({ regions, snapshots, limit = 5, testid = "stress-leaderboard" }) {
  const rows = useMemo(() => {
    const snapMap = {};
    (snapshots || []).forEach((s) => { snapMap[s.region_id] = s; });
    return (regions || [])
      .map((r) => {
        const m = snapMap[r.id]?.metrics;
        const score = regionHealth(m);
        return { region: r, metrics: m, score };
      })
      .sort((a, b) => a.score - b.score)
      .slice(0, limit);
  }, [regions, snapshots, limit]);

  if (rows.length === 0) return null;

  return (
    <section className="eid-card p-5 space-y-3" data-testid={testid}
             style={{ borderLeft: "3px solid #FF5500" }}>
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="p-2 rounded-sm bg-[#FF5500]/10 text-[#FF7A45]">
            <Warning size={20} weight="regular" />
          </span>
          <div>
            <div className="eid-label">Heute besonders beobachten</div>
            <h2 className="text-lg font-medium tracking-tight">Top {rows.length} Stress-Regionen</h2>
          </div>
        </div>
        <Link
          to="/map"
          className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
          data-testid="stress-to-map"
        >
          Auf Karte <ArrowRight size={12} />
        </Link>
      </header>

      <ol className="space-y-2">
        {rows.map((row, i) => {
          const band = healthBand(row.score);
          const tone = toneColor(band.level);
          const ns = ndviStatus(row.metrics?.ndvi_current);
          const ss = soilMoistureStatus(row.metrics?.soil_moisture_current);
          const ys = yieldStatus(row.metrics?.yield_forecast_index);
          // pick the most critical of the three, fall back to ns
          const critical = [ns, ss, ys].reduce((worst, s) => {
            const order = { critical: 3, warn: 2, ok: 1, good: 0, muted: 0 };
            return order[s?.level || "muted"] > order[worst?.level || "muted"] ? s : worst;
          }, ns);
          return (
            <li
              key={row.region.id}
              className="flex items-start gap-3 p-3 rounded-sm bg-[#161616] border border-white/10"
              data-testid={`stress-item-${row.region.id}`}
            >
              <span
                className="font-mono text-xs w-6 shrink-0 text-center py-0.5 rounded-full"
                style={{ background: tone.bg, color: tone.fg }}
              >
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <MapPin size={12} className="text-zinc-500 shrink-0" />
                    <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">
                      {row.region.country} · {row.region.bundesland}
                    </span>
                  </div>
                  <span
                    className="font-mono text-xs px-2 py-0.5 rounded-full"
                    style={{ background: tone.bg, color: tone.fg }}
                  >
                    Score {row.score} · {band.label}
                  </span>
                </div>
                <div className="font-medium text-white mt-0.5">{row.region.label}</div>
                {critical?.tip && (
                  <div className="text-xs text-zinc-400 mt-1 flex items-start gap-1.5">
                    <span
                      className="inline-block w-1 h-1 rounded-full mt-1.5 shrink-0"
                      style={{ background: tone.dot }}
                    />
                    <span>
                      <strong className="text-white font-medium">{critical.label}.</strong>{" "}
                      {critical.tip}
                    </span>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
